import { TextInput, TextInputProps } from 'components/forms/TextInput';
import { BaseInlineEditorProps, InlineEditor } from 'components/inline_edit/InlineEditor';
import { useSerializedInlineEditor } from 'custom_hooks/useInlineEditor';
import styled from 'styled-components';

type Value<TKey extends string> = { key: TKey; value: string | undefined };
type Values<TKey extends string> = Record<TKey, string | undefined>;
export type Template<TKey extends string> = TKey[];

const Container = styled.div`
    display: grid;
    width: 100%;
    gap: 2rem;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
`;

const deserialize = (v: string | undefined) => {
    try {
        return JSON.parse(v ?? '');
    } catch {
        return undefined;
    }
};
const serialize = <TKey extends string>(v: Values<TKey> | undefined) => JSON.stringify(v);

export type MultiTextInlineEditorProps<TKey extends string> = Omit<
    BaseInlineEditorProps<Value<TKey>, string>,
    | 'serialize'
    | 'deserialize'
    | 'renderValue'
    | 'value'
    | 'validate'
    | 'onConfirm'
    | 'formatInput'
> &
    Readonly<{
        /**
         * Optional text input config.
         */
        textInputConfig?: Partial<Omit<TextInputProps, 'value' | 'onChange'>>;
        /**
         * Optional function for formatting the input
         */
        formatInput?: (value: string | undefined) => string | undefined;
        /**
         * Optional type such as "text", "number".
         */
        type?: 'text' | 'search' | 'email' | 'password' | 'hidden' | 'number';
        template: Template<TKey>;
        value: Values<TKey>;
        validate?: (value: Values<TKey> | undefined) => string | undefined;
        onConfirm: (value: Values<TKey> | undefined) => void;
        renderValue?: (value: Values<TKey> | undefined) => React.ReactNode;
    }>;

/**
 * This generic text inline editor is meant to be used as a base for text
 * inline editors that convert a type to a string and back again.
 * See `TextInlineEditor` and `NumberInlineEditor`.
 */
export function MultiTextInlineEditor<TKey extends string>({
    value,
    renderValue,
    validate,
    onConfirm,
    onClose,
    isDisabled,
    editorClass,
    containerClass,
    containerSizing,
    editorStyle,
    valueStyle,
    textInputConfig,
    valueClass,
    type,
    ariaLabel,
    template,
}: MultiTextInlineEditorProps<TKey>) {
    const {
        valueToRender,
        inputValue,
        error,
        setInputValue,
        isEditing,
        isConfirming,
        openEditor,
        closeEditor,
        confirm,
        canConfirm,
    } = useSerializedInlineEditor<Values<TKey>, string>({
        value,
        validate,
        onConfirm,
        onClose,
        deserialize,
        serialize,
    });

    const title = template.map((key) => valueToRender?.[key]).join(' ');

    return (
        <>
            <InlineEditor
                ariaLabel={ariaLabel}
                value={renderValue ? renderValue(valueToRender) : title}
                editor={
                    <Container title={title}>
                        {template.map((key) => (
                            <TextInput
                                key={key}
                                type={type}
                                className="inline-block full-width"
                                {...textInputConfig}
                                value={deserialize(inputValue)?.[key]}
                                invalid={error !== undefined}
                                onChange={(e) => {
                                    const part = e.target.value;
                                    const v = deserialize(inputValue);
                                    v[key] = part;
                                    const serialized = serialize(v);
                                    setInputValue(serialized);
                                }}
                                onKeyDown={(event) => event.stopPropagation()}
                                disabled={isDisabled}
                            />
                        ))}
                    </Container>
                }
                containerSizing={containerSizing}
                valueClass={valueClass}
                editorClass={'flex flex-grow-1 full-width ' + editorClass}
                containerClass={containerClass}
                editorStyle={editorStyle}
                valueStyle={valueStyle}
                error={error}
                canClose={!isConfirming}
                canEdit={!isConfirming}
                canConfirm={canConfirm}
                isEditing={isEditing}
                isDisabled={isDisabled || isConfirming}
                isPending={isConfirming}
                onClose={closeEditor}
                onEdit={openEditor}
                onConfirm={confirm}
            />
        </>
    );
}
